import React, { useState, useEffect } from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { X, Plus, Trash2, FolderPlus, ChevronRight, ChevronDown, Edit2, Check, XCircle } from 'lucide-react';
import { db } from '../../services/database';

interface CategoryManagementModalProps {
  onClose: () => void;
  onCategoryUpdate: () => void;
}

interface Category {
  name: string;
  subcategories: string[];
  isExpanded?: boolean;
  isEditing?: boolean;
  editName?: string;
}

export const CategoryManagementModal: React.FC<CategoryManagementModalProps> = ({ onClose, onCategoryUpdate }) => {
  const [categories, setCategories] = useState<Record<string, Category>>({});
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  // const [selectedCategory, setSelectedCategory] = useState<string | null>(null); // TODO: Implement category selection feature
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [isAddingSubcategory, setIsAddingSubcategory] = useState<string | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<{category: string, subcategory: string, newName: string} | null>(null);

  useEffect(() => {
    loadCategories();
  }, []);

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const loadCategories = async () => {
    // Load existing categories from localStorage
    const savedCategories = localStorage.getItem('cleanRoomCategories');
    const categoryNames = savedCategories ? JSON.parse(savedCategories) : ['What I am working on', 'What I am learning', 'What I am interested'];
    
    // Load saved subcategories from localStorage
    const savedSubcategories = localStorage.getItem('cleanRoomSubcategories');
    const subcategoriesMap = savedSubcategories ? JSON.parse(savedSubcategories) : {};
    
    // Load items from the database to extract any additional subcategories
    const items = await db.getAllWorkItems();
    const categoryMap: Record<string, Category> = {};
    
    // Initialize with saved categories and their subcategories
    categoryNames.forEach((name: string) => {
      categoryMap[name] = {
        name,
        subcategories: subcategoriesMap[name] || [],
        isExpanded: true
      };
    });
    
    // Extract subcategories from existing items (in case there are any not in localStorage)
    items.forEach(item => {
      if (!categoryMap[item.level1]) {
        categoryMap[item.level1] = {
          name: item.level1,
          subcategories: [],
          isExpanded: true
        };
      }
      if (item.level2 && item.level2 !== 'Items' && !categoryMap[item.level1].subcategories.includes(item.level2)) {
        categoryMap[item.level1].subcategories.push(item.level2);
      }
    });
    
    // Save the complete subcategories map back to localStorage
    const completeSubcategoriesMap: Record<string, string[]> = {};
    Object.entries(categoryMap).forEach(([cat, data]) => {
      if (data.subcategories.length > 0) {
        completeSubcategoriesMap[cat] = data.subcategories;
      }
    });
    if (Object.keys(completeSubcategoriesMap).length > 0) {
      localStorage.setItem('cleanRoomSubcategories', JSON.stringify(completeSubcategoriesMap));
    }
    
    setCategories(categoryMap);
  };

  const handleAddCategory = () => {
    if (newCategoryName.trim() && !categories[newCategoryName]) {
      const updatedCategories = {
        ...categories,
        [newCategoryName]: {
          name: newCategoryName,
          subcategories: [],
          isExpanded: true
        }
      };
      setCategories(updatedCategories);
      
      // Save to localStorage
      const categoryNames = Object.keys(updatedCategories);
      localStorage.setItem('cleanRoomCategories', JSON.stringify(categoryNames));
      
      setNewCategoryName('');
      setIsAddingCategory(false);
      onCategoryUpdate();
    }
  };

  const handleAddSubcategory = (categoryName: string) => {
    const trimmedName = newSubcategoryName.trim();
    if (trimmedName && !categories[categoryName].subcategories.includes(trimmedName)) {
      const updatedCategories = {
        ...categories,
        [categoryName]: {
          ...categories[categoryName],
          subcategories: [...categories[categoryName].subcategories, trimmedName]
        }
      };
      setCategories(updatedCategories);
      
      // Save subcategories to localStorage
      const subcategoriesMap: Record<string, string[]> = {};
      Object.entries(updatedCategories).forEach(([cat, data]) => {
        if (data.subcategories.length > 0) {
          subcategoriesMap[cat] = data.subcategories;
        }
      });
      localStorage.setItem('cleanRoomSubcategories', JSON.stringify(subcategoriesMap));
      
      setNewSubcategoryName('');
      setIsAddingSubcategory(null);
      onCategoryUpdate();
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    const items = await db.getAllWorkItems();
    const itemsInCategory = items.filter(item => item.level1 === categoryName);
    
    if (itemsInCategory.length > 0) {
      if (!window.confirm(`Category "${categoryName}" contains ${itemsInCategory.length} items. Are you sure you want to delete it? All items will be moved to "Uncategorized".`)) {
        return;
      }
      
      // Move items to Uncategorized
      for (const item of itemsInCategory) {
        item.level1 = 'Uncategorized';
        await db.saveWorkItem(item);
      }
    }
    
    const updatedCategories = { ...categories };
    delete updatedCategories[categoryName];
    
    // Ensure at least one category exists
    if (Object.keys(updatedCategories).length === 0) {
      updatedCategories['Uncategorized'] = {
        name: 'Uncategorized',
        subcategories: [],
        isExpanded: true
      };
    }
    
    setCategories(updatedCategories);
    
    // Save to localStorage
    const categoryNames = Object.keys(updatedCategories);
    localStorage.setItem('cleanRoomCategories', JSON.stringify(categoryNames));
    onCategoryUpdate();
  };

  const handleDeleteSubcategory = async (categoryName: string, subcategoryName: string) => {
    const items = await db.getAllWorkItems();
    const itemsInSubcategory = items.filter(item => 
      item.level1 === categoryName && item.level2 === subcategoryName
    );
    
    if (itemsInSubcategory.length > 0) {
      if (!window.confirm(`Subcategory "${subcategoryName}" contains ${itemsInSubcategory.length} items. Items will be moved to the main category. Continue?`)) {
        return;
      }
      
      // Move items to main category
      for (const item of itemsInSubcategory) {
        item.level2 = 'Items';
        await db.saveWorkItem(item);
      }
    }
    
    const updatedCategories = {
      ...categories,
      [categoryName]: {
        ...categories[categoryName],
        subcategories: categories[categoryName].subcategories.filter(sub => sub !== subcategoryName)
      }
    };
    
    setCategories(updatedCategories);
    
    // Save subcategories to localStorage
    localStorage.setItem('cleanRoomSubcategories', JSON.stringify(
      Object.entries(updatedCategories).reduce((acc, [cat, data]) => ({
        ...acc,
        [cat]: data.subcategories
      }), {})
    ));
    
    onCategoryUpdate();
  };

  const handleRenameSubcategory = async (categoryName: string, oldSubcategoryName: string, newSubcategoryName: string) => {
    const trimmedName = newSubcategoryName.trim();
    
    // Validation
    if (!trimmedName || trimmedName === oldSubcategoryName) {
      setEditingSubcategory(null);
      return;
    }
    
    if (categories[categoryName].subcategories.includes(trimmedName)) {
      alert(`Subcategory "${trimmedName}" already exists in ${categoryName}`);
      return;
    }
    
    // Update items in database
    const items = await db.getAllWorkItems();
    for (const item of items) {
      if (item.level1 === categoryName && item.level2 === oldSubcategoryName) {
        item.level2 = trimmedName;
        await db.saveWorkItem(item);
      }
    }
    
    // Update subcategories
    const updatedCategories = {
      ...categories,
      [categoryName]: {
        ...categories[categoryName],
        subcategories: categories[categoryName].subcategories.map(sub => 
          sub === oldSubcategoryName ? trimmedName : sub
        )
      }
    };
    
    setCategories(updatedCategories);
    
    // Save to localStorage
    const subcategoriesMap: Record<string, string[]> = {};
    Object.entries(updatedCategories).forEach(([cat, data]) => {
      if (data.subcategories.length > 0) {
        subcategoriesMap[cat] = data.subcategories;
      }
    });
    localStorage.setItem('cleanRoomSubcategories', JSON.stringify(subcategoriesMap));
    
    setEditingSubcategory(null);
    onCategoryUpdate();
  };

  const handleRenameCategory = async (oldName: string, newName: string) => {
    const trimmedName = newName.trim();
    
    // Validation
    if (!trimmedName) {
      // Empty name, just cancel
      cancelEditingCategory(oldName);
      return;
    }
    
    if (trimmedName === oldName) {
      // No change, just cancel
      cancelEditingCategory(oldName);
      return;
    }
    
    if (categories[trimmedName]) {
      // Name already exists
      alert(`Category "${trimmedName}" already exists`);
      return;
    }
    
    // Update items in database
    const items = await db.getAllWorkItems();
    for (const item of items) {
      if (item.level1 === oldName) {
        item.level1 = trimmedName;
        await db.saveWorkItem(item);
      }
    }
    
    // Update categories
    const updatedCategories: Record<string, Category> = {};
    Object.entries(categories).forEach(([key, value]) => {
      if (key === oldName) {
        updatedCategories[trimmedName] = { ...value, name: trimmedName, isEditing: false, editName: undefined };
      } else {
        updatedCategories[key] = value;
      }
    });
    
    setCategories(updatedCategories);
    
    // Save to localStorage
    const categoryNames = Object.keys(updatedCategories);
    localStorage.setItem('cleanRoomCategories', JSON.stringify(categoryNames));
    onCategoryUpdate();
  };

  const toggleCategoryExpansion = (categoryName: string) => {
    setCategories({
      ...categories,
      [categoryName]: {
        ...categories[categoryName],
        isExpanded: !categories[categoryName].isExpanded
      }
    });
  };

  const startEditingCategory = (categoryName: string) => {
    setCategories({
      ...categories,
      [categoryName]: {
        ...categories[categoryName],
        isEditing: true,
        editName: categoryName
      }
    });
  };

  const cancelEditingCategory = (categoryName: string) => {
    setCategories({
      ...categories,
      [categoryName]: {
        ...categories[categoryName],
        isEditing: false,
        editName: undefined
      }
    });
  };

  return (
    <div style={styles.components.categoryModal.overlay} onClick={onClose}>
        <div style={styles.components.categoryModal.container} onClick={(e) => e.stopPropagation()}>
        <div style={styles.components.categoryModal.header}>
          <div>
            <h2 style={styles.components.categoryModal.title}>Category Management</h2>
            <p style={styles.components.categoryModal.subtitle}>Organize your categories and subcategories</p>
          </div>
          <button
            onClick={onClose}
            style={styles.components.categoryModal.closeButton}
          >
            <X size={20} style={{ color: '#6b7280' }} />
          </button>
        </div>

        <div style={styles.components.categoryModal.content}>
          {Object.entries(categories).map(([categoryName, category]) => (
            <div key={categoryName} style={styles.components.categoryModal.categoryCard}>
              <div style={{
                ...styles.components.categoryModal.categoryHeader,
                ...(category.isExpanded ? styles.components.categoryModal.categoryHeaderExpanded : {})
              }}>
                <div style={styles.components.categoryModal.categoryName}>
                  <button
                    onClick={() => toggleCategoryExpansion(categoryName)}
                    style={styles.components.categoryModal.expandButton}
                  >
                    {category.isExpanded ? (
                      <ChevronDown size={16} />
                    ) : (
                      <ChevronRight size={16} />
                    )}
                  </button>
                  
                  {category.isEditing ? (
                    <div style={{
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap2,
     flex: 1 
  }}>
                      <input
                        type="text"
                        value={category.editName}
                        onChange={e => setCategories({
                          ...categories,
                          [categoryName]: {
                            ...category,
                            editName: e.target.value
                          }
                        })}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const newName = category.editName || '';
                            if (newName === categoryName || newName.trim() === '') {
                              // No change or empty, just cancel editing
                              cancelEditingCategory(categoryName);
                            } else {
                              handleRenameCategory(categoryName, newName);
                            }
                          } else if (e.key === 'Escape') {
                            cancelEditingCategory(categoryName);
                          }
                        }}
                        style={{
    ...styles.spacing.px2,
    ...styles.utility.border,
    ...styles.utility.rounded,
    ...styles.typography.textSm,
     flex: 1 
  }}
                        autoFocus
                      />
                      <button
                        onClick={() => {
                          const newName = category.editName || '';
                          if (newName === categoryName || newName.trim() === '') {
                            cancelEditingCategory(categoryName);
                          } else {
                            handleRenameCategory(categoryName, newName);
                          }
                        }}
                        style={styles.button.iconSuccess}
                        title="Save changes"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={() => cancelEditingCategory(categoryName)}
                        style={{
                          ...styles.components.categoryModal.actionButton,
                          ...styles.components.categoryModal.actionButtonDanger
                        }}
                      >
                        <XCircle size={16} />
                      </button>
                    </div>
                  ) : (
                    <span style={styles.components.categoryModal.categoryName}>{categoryName}</span>
                  )}
                </div>
                
                {!category.isEditing && (
                  <div style={styles.components.categoryModal.categoryActions}>
                    <button
                      onClick={() => startEditingCategory(categoryName)}
                      style={styles.components.categoryModal.actionButton}
                      title="Rename category"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => setIsAddingSubcategory(categoryName)}
                      style={styles.components.categoryModal.actionButton}
                      title="Add subcategory"
                    >
                      <FolderPlus size={14} />
                    </button>
                    {Object.keys(categories).length > 1 && (
                      <button
                        onClick={() => handleDeleteCategory(categoryName)}
                        style={{
                          ...styles.components.categoryModal.actionButton,
                          ...styles.components.categoryModal.actionButtonDanger
                        }}
                        title="Delete category"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                )}
              </div>
              
              {category.isExpanded && (
                <div style={{
    ...styles.spacing.paddingLg,
     gap: "var(--space-2)" 
  }}>
                  {category.subcategories.map(subcategory => (
                    <div key={subcategory} style={{
    ...styles.layout.flex,
     alignItems: "center" ,
     justifyContent: "space-between" ,
    ...styles.spacing.py2,
    ...styles.utility.rounded
  }}>
                      {editingSubcategory?.category === categoryName && editingSubcategory?.subcategory === subcategory ? (
                        <div style={{
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap2,
     flex: 1 
  }}>
                          <input
                            type="text"
                            value={editingSubcategory.newName}
                            onChange={e => setEditingSubcategory({
                              ...editingSubcategory,
                              newName: e.target.value
                            })}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                handleRenameSubcategory(categoryName, subcategory, editingSubcategory.newName);
                              } else if (e.key === 'Escape') {
                                setEditingSubcategory(null);
                              }
                            }}
                            style={{
    ...styles.spacing.px2,
    ...styles.utility.border,
    ...styles.utility.rounded,
    ...styles.typography.textSm,
     flex: 1 
  }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleRenameSubcategory(categoryName, subcategory, editingSubcategory.newName)}
                            style={styles.button.iconSuccess}
                            title="Save"
                          >
                            <Check size={14} />
                          </button>
                          <button
                            onClick={() => setEditingSubcategory(null)}
                            style={styles.button.iconDanger}
                            title="Cancel"
                          >
                            <XCircle size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span style={{
    ...styles.typography.textSm,
     color: "var(--color-gray-700)" 
  }}>{subcategory}</span>
                          <div style={{...styles.utility.opacity90, 
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap1
  }}>
                            <button
                              onClick={() => setEditingSubcategory({
                                category: categoryName,
                                subcategory: subcategory,
                                newName: subcategory
                              })}
                              style={styles.button.iconSmall}
                              title="Rename subcategory"
                            >
                              <Edit2 size={12} style={{
     color: "var(--color-gray-600)" 
  }} />
                            </button>
                            <button
                              onClick={() => handleDeleteSubcategory(categoryName, subcategory)}
                              style={styles.button.iconDanger}
                              title="Delete subcategory"
                            >
                              <Trash2 size={12} style={{
     color: "#dc2626" 
  }} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  
                  {isAddingSubcategory === categoryName ? (
                    <div style={{...styles.extended.pl8, 
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap2
  }}>
                      <input
                        type="text"
                        value={newSubcategoryName}
                        onChange={e => setNewSubcategoryName(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleAddSubcategory(categoryName)}
                        placeholder="Subcategory name..."
                        style={{...styles.utility.border, 
     flex: 1 ,
    ...styles.spacing.px3,
    ...styles.spacing.py2,
    ...styles.utility.border,
    ...styles.utility.rounded,
    ...styles.typography.textSm
  }}
                        autoFocus
                      />
                      <button
                        onClick={() => handleAddSubcategory(categoryName)}
                        style={{
                          ...styles.button.primary,
                          ...styles.button.small
                        }}
                      >
                        Add
                      </button>
                      <button
                        onClick={() => {
                          setIsAddingSubcategory(null);
                          setNewSubcategoryName('');
                        }}
                        style={{
                          ...styles.button.cancel,
                          ...styles.button.small
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    category.subcategories.length === 0 && (
                      <div style={{...styles.typography.textTertiary, ...styles.typography.textSm}}>
                        No subcategories yet
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          ))}
          {isAddingCategory ? (
            <div style={{
    ...styles.layout.flex,
     alignItems: "center" ,
    ...styles.spacing.gap2,
    ...styles.spacing.paddingLg,
    ...styles.utility.roundedLg
  }}>
              <input
                type="text"
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleAddCategory()}
                placeholder="Category name..."
                style={{...styles.utility.border, 
     flex: 1 ,
    ...styles.spacing.px3,
    ...styles.spacing.py2,
    ...styles.utility.border,
    ...styles.utility.rounded
  }}
                autoFocus
              />
              <button
                onClick={handleAddCategory}
                style={styles.button.primary}
              >
                Add Category
              </button>
              <button
                onClick={() => {
                  setIsAddingCategory(false);
                  setNewCategoryName('');
                }}
                style={styles.button.cancel}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsAddingCategory(true)}
              style={styles.components.categoryModal.addButton}
            >
              <Plus size={20} />
              <span>Add New Category</span>
            </button>
          )}
        </div>

        <div style={{
          paddingTop: '1.5rem',
          display: 'flex',
          justifyContent: 'center',
          marginTop: '1.5rem',
          borderTop: '1px solid #e5e7eb'
        }}>
          <button
            onClick={onClose}
            style={{
              ...styles.components.categoryModal.addButtonPrimary,
              width: '100%'
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};